import { atom } from 'jotai'

import { SelectedAddon } from '@/app/[locale]/[ferry]/extras/_components/interface'

import { atomWithStorage, createJSONStorage } from 'jotai/utils'

import {
  ConfirmingState,
  SelectedAddons,
  SelectedDate,
  SelectedDepartures,
  SelectedOffer,
  SelectedPassengers,
  SelectedPayment,
  SelectedPets,
  SelectedRoutes,
  SelectedVehicles,
} from './selection.interface'

// Use sessionStorage for persistence
const storage = createJSONStorage<Selections>(() => sessionStorage)

// Parent atom to store the "selections" state
interface Selections {
  ConfirmingState: ConfirmingState
  SelectedAddons: SelectedAddons
  SelectedDate: SelectedDate
  SelectedDepartures: SelectedDepartures
  SelectedOfferCode: SelectedOffer
  SelectedPassengers: SelectedPassengers
  SelectedPaymentType: SelectedPayment
  SelectedPets: SelectedPets
  SelectedRoutes: SelectedRoutes
  SelectedVehicles: SelectedVehicles
}

export const selections = atomWithStorage<Selections>(
  'selections',
  {
    ConfirmingState: 'CONFIRMING', // Add confirmingState
    SelectedAddons: { outboundAddons: [], returnAddons: [] },
    SelectedDate: { outboundDepartureDate: null, returnDepartureDate: null },
    SelectedDepartures: { departureHome: null, departureOut: null },
    SelectedOfferCode: { offerCode: undefined },
    SelectedPassengers: { adults: 1, children: 0, childrenAges: [], completed: false, infants: 0 },
    SelectedPaymentType: { completed: false, paymentType: undefined },
    SelectedPets: { pets: [], showNumberOfPets: 0 },
    SelectedRoutes: { homebound: null, outbound: '' },
    SelectedVehicles: { completed: false, vehicleCode: '' },
  },
  storage
)

//SelectedDate

export const selectedDateAtom = atom(
  (get) => get(selections).SelectedDate, // Access SelectedDate from the parent atom
  (get, set, updatedValue: SelectedDate) => {
    const current = get(selections) // Get the current parent state
    set(selections, { ...current, SelectedDate: updatedValue }) // Update only SelectedDate
  }
)

export const selectedDeparturesAtom = atom(
  (get) => get(selections).SelectedDepartures, // Access SelectedDepartures from parent atom
  (get, set, updatedValue: SelectedDepartures) => {
    const current = get(selections)
    set(selections, { ...current, SelectedDepartures: updatedValue }) // Update only SelectedDepartures
  }
)

export const confirmingAtom = atom(
  (get) => get(selections).ConfirmingState, // Access ConfirmingState
  (get, set, updatedValue: ConfirmingState) => {
    const current = get(selections)
    set(selections, { ...current, ConfirmingState: updatedValue }) // Update ConfirmingState
  }
)

export const selectedAddonsAtom = atom(
  (get) => get(selections).SelectedAddons,
  (get, set, updatedValue: { outboundAddons: SelectedAddon[]; returnAddons: SelectedAddon[] }) => {
    const current = get(selections)
    set(selections, { ...current, SelectedAddons: updatedValue })
  }
)

export const selectedPetsAtom = atom(
  (get) => get(selections).SelectedPets,
  (get, set, updatedValue: SelectedPets) => {
    const current = get(selections)
    set(selections, { ...current, SelectedPets: updatedValue })
  }
)

export const selectedVehiclesAtom = atom(
  (get) => get(selections).SelectedVehicles,
  (get, set, updatedValue: SelectedVehicles) => {
    const current = get(selections)
    set(selections, { ...current, SelectedVehicles: updatedValue })
  }
)

export const selectedRoutesAtom = atom(
  (get) => get(selections).SelectedRoutes,
  (get, set, updatedValue: SelectedRoutes) => {
    const current = get(selections)
    set(selections, { ...current, SelectedRoutes: updatedValue })
  }
)

export const selectedPaymentAtom = atom(
  (get) => get(selections).SelectedPaymentType,
  (get, set, updatedValue: SelectedPayment) => {
    const current = get(selections)
    set(selections, { ...current, SelectedPaymentType: updatedValue })
  }
)

export const selectedOfferAtom = atom(
  (get) => get(selections).SelectedOfferCode,
  (get, set, updatedValue: SelectedOffer | ((prev: SelectedOffer) => SelectedOffer)) => {
    const current = get(selections)
    // Resolve updater against the latest value so concurrent writes are not overwritten.
    const next = typeof updatedValue === 'function' ? updatedValue(current.SelectedOfferCode) : updatedValue
    set(selections, { ...current, SelectedOfferCode: next })
  }
)

export const selectedPassengersAtom = atom(
  (get) => get(selections).SelectedPassengers,
  (get, set, updatedValue: SelectedPassengers) => {
    const current = get(selections)
    set(selections, { ...current, SelectedPassengers: updatedValue })
  }
)
